import { useEffect, useState, useCallback, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export type AppRole = 'client' | 'agent' | 'manager' | 'admin';
export type PortalType = 'internal' | 'client' | 'none';
export type InternalRole = 'super_admin' | 'account_manager' | 'operations_manager' | 'team_leader' | 'qa_specialist' | 'wfm_planner' | 'trainer_kb_manager' | 'it_delivery_manager' | 'incident_manager' | 'finance_admin' | 'agent';
export type ClientRole = 'owner' | 'admin' | 'operations_manager' | 'qa_trainer' | 'finance' | 'viewer';

// Internal roles that have management/supervisory permissions (can access admin portal)
const INTERNAL_MANAGER_ROLES: InternalRole[] = [
  'super_admin',
  'account_manager', 
  'operations_manager',
  'team_leader',
  'qa_specialist',
  'wfm_planner',
  'trainer_kb_manager',
  'it_delivery_manager',
  'incident_manager',
  'finance_admin'
];

// Check if an internal role has admin portal access
export function isInternalRoleManager(internalRole: InternalRole | null): boolean {
  if (!internalRole) return false;
  return INTERNAL_MANAGER_ROLES.includes(internalRole);
}

export interface Profile {
  id: string;
  full_name: string;
  avatar_url: string | null;
  company_name: string | null;
  phone: string | null;
  timezone: string;
  language: string;
  is_active: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface UserAuthInfo {
  effective_role: AppRole;
  portal_type: PortalType;
  is_internal: boolean;
  internal_role: InternalRole | null;
  client_role: ClientRole | null;
  client_org_id: string | null;
  app_roles: AppRole[];
}

export interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  role: AppRole | null;
  authInfo: UserAuthInfo | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isStaff: boolean;
  isManagerOrAdmin: boolean;
  isInternalStaff: boolean;
  isInternalManager: boolean; // Internal staff with admin portal access (not agents)
}

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string, role?: AppRole, companyName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [authInfo, setAuthInfo] = useState<UserAuthInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserData = useCallback(async (userId: string) => {
    try {
      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        return;
      }

      if (profileData) {
        setProfile(profileData as Profile);
      }

      // Fetch comprehensive auth info using new RPC function
      const { data: authInfoData, error: authInfoError } = await supabase
        .rpc('get_user_auth_info', { _user_id: userId });

      if (authInfoError) {
        console.error('Error fetching auth info:', authInfoError);
        // Fallback to old method
        const { data: roleData } = await supabase.rpc('get_user_role', { _user_id: userId });
        if (roleData) {
          setRole(roleData as AppRole);
        }
        return;
      }

      if (authInfoData) {
        const info = authInfoData as unknown as UserAuthInfo;
        setAuthInfo(info);
        setRole(info.effective_role);
      }
    } catch (err) {
      console.error('Error in fetchUserData:', err);
    }
  }, []);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (currentSession?.user) {
          // Defer profile fetch to avoid deadlocks
          setTimeout(() => {
            fetchUserData(currentSession.user.id);
          }, 0);
        } else {
          setProfile(null);
          setRole(null);
          setAuthInfo(null);
        }
        
        if (event === 'SIGNED_OUT') {
          setProfile(null);
          setRole(null);
          setAuthInfo(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      
      if (existingSession?.user) {
        fetchUserData(existingSession.user.id).finally(() => {
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchUserData]);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        return { error };
      }
      
      // CRITICAL: Broadcast force logout to all other instances on successful login
      // This ensures single session enforcement at the authentication level
      if (data?.user?.id) {
        broadcastSingleSessionEnforcement(data.user.id);
      }
      
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };
  
  // Helper function to broadcast single session enforcement
  const broadcastSingleSessionEnforcement = async (userId: string) => {
    const instanceId = getOrCreateInstanceId();
    const channelName = `agent_session_${userId}`;
    
    const channel = supabase.channel(channelName);
    
    await channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel.send({
          type: 'broadcast',
          event: 'force_logout',
          payload: {
            agent_id: userId,
            new_instance_id: instanceId,
            timestamp: new Date().toISOString(),
            device_info: navigator.userAgent,
            trigger: 'login', // Distinguish from clock-in triggered force logout
          },
        });
        
        // Small delay before unsubscribing to ensure message is sent
        setTimeout(() => {
          supabase.removeChannel(channel);
        }, 500);
      }
    });
  };
  
  // Get or create a unique instance ID for this browser tab
  const getOrCreateInstanceId = (): string => {
    const existing = sessionStorage.getItem('agent_instance_id');
    if (existing) return existing;
    
    const newId = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem('agent_instance_id', newId);
    return newId;
  };

  const signUp = async (
    email: string, 
    password: string, 
    fullName: string, 
    userRole: AppRole = 'client',
    companyName?: string
  ) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
            role: userRole,
            company_name: companyName,
          }
        }
      });
      
      if (error) {
        return { error };
      }

      // Profile and role are created automatically by the handle_new_user trigger
      // Do NOT duplicate inserts here - it causes unique constraint violations
      
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signOut = async () => {
    // Reset Chatwoot before signing out
    if (window.$chatwoot) {
      try {
        window.$chatwoot.reset();
      } catch (e) {
        console.warn("[BPO Contact] Chatwoot reset failed:", e);
      }
    }
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRole(null);
    setAuthInfo(null);
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchUserData(user.id);
    }
  };

  const isAuthenticated = !!user && !!session;
  const isStaff = role === 'agent' || role === 'manager' || role === 'admin';
  const isManagerOrAdmin = role === 'manager' || role === 'admin';
  // Internal staff = has internal_user_roles entry (regardless of which internal role)
  const isInternalStaff = !!authInfo?.is_internal;
  // Internal staff with management permissions (not internal agents) - can access admin portal
  // IMPORTANT: Only check internal_role from authInfo, NOT the app role
  const isInternalManager = authInfo?.is_internal 
    ? isInternalRoleManager(authInfo.internal_role as InternalRole | null) 
    : (role === 'admin' || role === 'manager');

  const value: AuthContextType = {
    user,
    session,
    profile,
    role,
    authInfo,
    isLoading,
    isAuthenticated,
    isStaff,
    isManagerOrAdmin,
    isInternalStaff,
    isInternalManager,
    signIn,
    signUp,
    signOut,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper function to get redirect path based on role and auth info
export function getRedirectPath(role: AppRole | null, authInfo?: UserAuthInfo | null): string {
  // If user is internal manager (not agent), go to admin
  if (authInfo?.is_internal && isInternalRoleManager(authInfo?.internal_role as InternalRole | null)) {
    return '/admin/dashboard';
  }
  
  // If internal agent, go to agent portal
  if (authInfo?.is_internal && authInfo?.internal_role === 'agent') {
    return '/en/agent';
  }

  switch (role) {
    case 'client':
      return '/en/candidate/dashboard';
    case 'agent':
      return '/en/agent';
    case 'manager':
    case 'admin':
      return '/admin/dashboard';
    default:
      if (authInfo?.portal_type === 'client') return '/en/candidate/dashboard';
      return '/en/auth';
  }
}

// Get all accessible paths for a role
export function getAccessiblePaths(role: AppRole | null, authInfo?: UserAuthInfo | null): string[] {
  const basePaths = ['/profile', '/settings'];
  
  // Internal managers have access to admin + agent portals
  if (authInfo?.is_internal && isInternalRoleManager(authInfo?.internal_role as InternalRole | null)) {
    return [...basePaths, '/admin', '/en/portal', '/client', '/agent', '/en/agent'];
  }
  
  // Internal agents only have access to agent portal
  if (authInfo?.is_internal && authInfo?.internal_role === 'agent') {
    return [...basePaths, '/agent', '/en/agent'];
  }
  
  switch (role) {
    case 'client':
      return [...basePaths, '/client', '/en/portal'];
    case 'agent':
      return [...basePaths, '/agent', '/en/agent'];
    case 'manager':
      return [...basePaths, '/admin', '/en/portal', '/agent', '/en/agent'];
    case 'admin':
      return [...basePaths, '/admin', '/en/portal', '/client', '/agent', '/en/agent'];
    default:
      return [];
  }
}

// Check if user can access a specific path
export function canAccessPath(path: string, role: AppRole | null, authInfo?: UserAuthInfo | null): boolean {
  const accessiblePaths = getAccessiblePaths(role, authInfo);
  return accessiblePaths.some(p => path.startsWith(p));
}
